/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Row,
  Col,
  Spin,
  Modal,
  Input,
  Button,
} from 'antd';

import { onError } from 'utils/handlers';
import Table from 'components/Table';
import API from 'utils/api';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import ModalDelete from 'components/ModalDelete';
import { hasActionPermission, ActionsNames } from 'utils/permission/index';
import {
  getEstadosFoliosPlacas,
  getEstadoFolioPlaca,
  patchEstadoFolioPlaca,
  postEstadoFolioPlaca,
  deleteEstadoFolioPlaca,
} from 'api/recaudacion/estadoFolioPlacas';

const baseURI = '/recaudacion/estados-de-folio-placas-tarjetas/';

const EstadoFolioPlaca = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [hasActionPermissions, setHasActionPermissions] = useState(true);
  const [formInfo] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    const _data = await getEstadosFoliosPlacas();
    setData(_data);
    setLoading(false);
  };

  const fetchAll = async () => {
    try {
      setLoading(true);
      await fetchData();
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  useEffect(() => {
    const validatePermission = () => {
      const _hasAllViewPermission = hasActionPermission('unidadresponsable', ActionsNames.view);
      setHasActionPermissions(_hasAllViewPermission);
    };
    validatePermission();
    fetchAll();
    return API.tryCancel;
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickEdit = async () => {
    const [key] = selectedRowKeys;
    const match = await getEstadoFolioPlaca(key);
    if (match) {
      setVisible(true);
      form.setFieldsValue(match);
      formInfo.setFieldsValue(match);
    }
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formInfo.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formInfo.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      const _data = await (key ? patchEstadoFolioPlaca(key, values) : postEstadoFolioPlaca(values));
      if (_data) {
        await fetchData();
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([_data.id]);
            setTimeout(() => onClickEdit(_data));
          } else {
            setTimeout(() => onClickEdit());
          }
        } else {
          onCancel();
        }
      }
      setLoading(false);
      return true;
    } catch (err) {
      onError(err, setLoading, [form, formInfo]);
      return false;
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteEstadoFolioPlaca(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  /*
  const reset = () => {
    setDepartamentoFound([]);
    form.resetFields(['q']);
    form.validateFields(['q']);
  };
  */
  const rules = {
    required: [
      {
        required: true,
        message: 'El campo es requerido',
      },
    ],
  };

  const columns = [
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 300,
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      render: (val) => (estadosGlobales.find((item) => item.id === val).descripcion),
      catalog: {
        cat: estadosGlobales,
        labelProp: 'descripcion',
      },
    },
  ];

  return (
    <Row align="center" justify="center" className="container">
      <Spin
        tip="Cargando..."
        spinning={loading}
      >
        <Table
          cols={columns}
          data={data}
          rowKey="id"
          baseURI={baseURI}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit,
            onClickDelete,
          }}
          loading={loading}
          hasAllViewPermission={hasActionPermissions}
          permission={permission}
        />
        <Modal
          visible={visible}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? ' Editar' : 'Agregar'} Estado de Folio de la Placa`}
              onCancel={onCancel}
              loading={loading}
              onFinish={onFinish}
              permission={permission}
            />
          )}
          keyboard={!loading}
          maskClosable={!loading}
          footer={null}
          closable={false}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            initialValues={{ estados_globales: 1 }}
          >
            <Row>
              <Col span={24}>
                <Form.Item
                  name="descripcion"
                  label="Descripción"
                  rules={rules.required}
                >
                  <Input allowClear />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="estados_globales"
                  rules={rules.required}
                  label="Estado"
                  hasFeedback
                >
                  <Select dataSource={estadosGlobales} disabled={!selectedRowKeys.length} />
                </Form.Item>
              </Col>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          visible={visibleAlert}
          content={`EstadoFolioPlaca ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default EstadoFolioPlaca;
